import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Card, CardBody, Spinner } from "reactstrap";
// import "../assets/scss/pages/_subscription-plan.scss";
import { PaywallPurchaseResult } from "@revenuecat/purchases-js";
import { useAppSelector, useAppDispatch } from "Components/Hooks/hooks";
import { setRevenueBillingInfo, updateUserPayload } from "slices/auth/reducer";
import { getPurchases, OFFERING_ID, PRODUCT_ID } from "helpers/purchases";
import CustomButton from "Components/Custom/CustomButton";
import { useNavigate } from "react-router-dom";

const SubscriptionPlan = () => {
  document.title = "Subscription Plan | EaziQuote";
  const { user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Initialize subscription state based on user data
  const [isPaywallLoading, setIsPaywallLoading] = useState(true);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(
    user?.is_subscription_active || false,
  );

  useEffect(() => {
    setIsSubscriptionActive(user?.is_subscription_active || false);
  }, [user?.is_subscription_active]);

  const showPaywall = useCallback(async () => {
    const handlePurchaseResult = (purchaseResult: PaywallPurchaseResult) => {
      // Purchase was successful - purchaseResult contains customerInfo
      console.log("Purchase successful!", purchaseResult);

      // Check if there are active subscriptions
      const hasActiveSubscription =
        purchaseResult.customerInfo.activeSubscriptions.size > 0;

      if (hasActiveSubscription) {
        // Update local state immediately to hide paywall
        setIsSubscriptionActive(true);

        // Clear paywall container
        const paywallContainer = document.getElementById("paywall-container");
        if (paywallContainer) {
          paywallContainer.innerHTML = "";
        }

        // Update user subscription status in Redux store
        dispatch(
          updateUserPayload({
            is_subscription_active: true,
            is_trial_period: false,
          }),
        );

        dispatch(
          setRevenueBillingInfo({
            info: purchaseResult.customerInfo,
            willRenew:
              purchaseResult.customerInfo.subscriptionsByProductIdentifier[
                PRODUCT_ID
              ]?.willRenew || false,
          }),
        );
        console.log(
          "Active subscriptions:",
          Array.from(purchaseResult.customerInfo.activeSubscriptions),
        );
        console.log("Entitlements:", purchaseResult.customerInfo.entitlements);
        console.log("Selected package:", purchaseResult.selectedPackage);
      }
    };

    // Check if user exists
    if (!user || !user.company) {
      setIsPaywallLoading(false);
      return;
    }

    try {
      const purchases = getPurchases(user.company.id.toString());

      // Also verify with RevenueCat to get the latest subscription status
      const customerInfo = await purchases.getCustomerInfo();
      console.log("customerInfoPPPPPPPP", customerInfo);
      const hasActiveSubscription = customerInfo.activeSubscriptions.size > 0;

      if (hasActiveSubscription) {
        // User has active subscription in RevenueCat, update Redux and show active state
        dispatch(
          updateUserPayload({
            is_subscription_active: true,
            is_trial_period: false,
          }),
        );
        setIsSubscriptionActive(true);
        setIsPaywallLoading(false);
        return;
      }

      const offerings = await purchases.getOfferings();
      console.log(offerings, purchases);

      const offeringToUse = offerings.all[OFFERING_ID];

      // Wait for next render cycle to ensure DOM is updated
      await new Promise((resolve) => setTimeout(resolve, 100));

      const paywallContainer = document.getElementById("paywall-container");

      if (paywallContainer) {
        try {
          // Set up a MutationObserver to detect when paywall content is rendered
          const hideLoaderWhenPaywallReady = () => {
            return new Promise<void>((resolve) => {
              const observer = new MutationObserver((mutations, obs) => {
                // Check if paywall container has meaningful content
                if (paywallContainer.children.length > 0) {
                  // Give a small delay for styles to apply
                  setTimeout(() => {
                    setIsPaywallLoading(false);
                    resolve();
                  }, 50);
                  obs.disconnect();
                }
              });

              observer.observe(paywallContainer, {
                childList: true,
                subtree: true,
              });

              // Fallback timeout in case observer doesn't trigger
              setTimeout(() => {
                observer.disconnect();
                setIsPaywallLoading(false);
                resolve();
              }, 5000);
            });
          };

          // Start observing for paywall content
          const paywallReadyPromise = hideLoaderWhenPaywallReady();

          const purchaseResult = await purchases.presentPaywall({
            htmlTarget: paywallContainer,
            offering: offeringToUse,
          });

          // Ensure loader is hidden (in case observer didn't trigger)
          await paywallReadyPromise;

          console.log("Paywall completed", purchaseResult);

          // Handle the successful purchase result
          handlePurchaseResult(purchaseResult);
        } catch (error: any) {
          // Handle purchase errors or cancellation
          if (error?.errorCode === "UserCancelledError") {
            console.log("Purchase was cancelled by user");
          } else {
            console.error("Error presenting paywall", error);
          }
          setIsPaywallLoading(false);
        }
      } else {
        setIsPaywallLoading(false);
      }
    } catch (error: any) {
      console.error("Error checking subscription:", error);
      setIsPaywallLoading(false);
    }
  }, [user, dispatch]);

  useEffect(() => {
    if (!isSubscriptionActive) {
      showPaywall();
    } else {
      // If subscription is active, don't show loader and clear any existing paywall
      setIsPaywallLoading(false);
      const paywallContainer = document.getElementById("paywall-container");
      if (paywallContainer) {
        paywallContainer.innerHTML = "";
      }
    }
  }, [showPaywall, isSubscriptionActive]);

  // Render content for active subscription
  const renderActiveSubscriptionContent = () => (
    <div className="text-center w-100 py-4 align-content-center d-flex flex-column">
      <div className="mb-4">
        <div
          className="bg-success bg-opacity-10 d-inline-flex justify-content-center align-items-center mb-3"
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-success"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
      </div>
      <h5 className="fw-semibold mb-2" style={{ color: "#2D2D2D" }}>
        Subscription Active
      </h5>
      <p className="text-muted mb-3">
        You have an active subscription. Enjoy all the premium features!
      </p>
      <CustomButton
        text="Go to Dashboard"
        newClassName="me-2 bg-primary  align-self-center"
        onClick={() => {
          navigate("/dashboard");
        }}
      />
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row className="d-flex justify-content-center align-items-center">
            <Col xs={12} lg={8} xl={7}>
              {/* Header Section */}
              <div className="text-center mb-5">
                <h2
                  className="fw-bold mb-3"
                  style={{ color: "#2D2D2D", fontSize: "32px" }}
                >
                  {isSubscriptionActive
                    ? "Your Subscription"
                    : "Plans & Pricing"}
                </h2>
                <p className="text-muted" style={{ fontSize: "16px" }}>
                  {isSubscriptionActive
                    ? "Manage your subscription and billing details."
                    : "Simple pricing with complete access to all features."}
                </p>
              </div>

              {/* Paywall Container */}
              <div className="d-flex justify-content-center ">
                {isSubscriptionActive ? (
                  <Card
                    className="shadow-sm border-0"
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <CardBody
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {renderActiveSubscriptionContent()}
                    </CardBody>
                  </Card>
                ) : (
                  <Card
                    className="shadow-sm border-0"
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <CardBody
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "300px",
                        position: "relative",
                      }}
                    >
                      {/* Show loader while loading - overlay on top */}
                      {isPaywallLoading && (
                        <div
                          className="text-center"
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "white",
                            zIndex: 10,
                          }}
                        >
                          <Spinner
                            color="primary"
                            style={{
                              width: "3rem",
                              height: "3rem",
                              borderWidth: "0.3em",
                            }}
                          />
                          <div className="mt-4">
                            <h5
                              className="fw-semibold mb-2"
                              style={{ color: "#2D2D2D" }}
                            >
                              Loading Plans
                            </h5>
                            <p
                              className="text-muted mb-0"
                              style={{ fontSize: "14px" }}
                            >
                              Please wait while we fetch your subscription
                              options...
                            </p>
                          </div>

                          {/* Loading dots animation */}
                          <div className="mt-3">
                            <span className="loading-dots">
                              <span
                                className="dot"
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: "#0d6efd",
                                  margin: "0 4px",
                                  animation:
                                    "loading 1.4s infinite ease-in-out both",
                                  animationDelay: "0s",
                                }}
                              ></span>
                              <span
                                className="dot"
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: "#0d6efd",
                                  margin: "0 4px",
                                  animation:
                                    "loading 1.4s infinite ease-in-out both",
                                  animationDelay: "0.2s",
                                }}
                              ></span>
                              <span
                                className="dot"
                                style={{
                                  display: "inline-block",
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: "#0d6efd",
                                  margin: "0 4px",
                                  animation:
                                    "loading 1.4s infinite ease-in-out both",
                                  animationDelay: "0.4s",
                                }}
                              ></span>
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Paywall container - always rendered so ref is available */}
                      <div
                        id="paywall-container"
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      />
                    </CardBody>
                  </Card>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>
        {`
          @keyframes loading {
            0%, 80%, 100% {
              transform: scale(0);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }

          .page-content {
            transition: background-color 0.3s ease;
          }

          .card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
          }
        `}
      </style>
    </React.Fragment>
  );
};

export default SubscriptionPlan;
